import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {CreateEmployeeCommand} from "../../../commands/create-employee.command";
import {ICommand} from "../../../commands/i-command.interface";
import {RenameEmployeeCommand} from "../../../commands/rename-employee.command";
import {Employee} from "../../../domain/entities/employee.entity";
import {AbstractEntityDialogComponent} from "../../ha-dialog/abstract-entity-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogCommand} from "../../ha-dialog/dialog-command.interface";
import {get, translate} from "lit-translate";
import {FormConfiguration} from "../../../advanced-form/form-configuration.type";
import {NotEmpty} from "../../../advanced-form/validators/not-empty.validator";
import {container} from "tsyringe";
import {WeeklyWorkingTimeRepository} from "../../../domain/repositories/weekly-working-time.repository";
import {CreateWeeklyWorkingTimeCommand} from "../../../commands/create-weekly-working-time.command";
import {UpdateWeeklyWorkingTimeCommand} from "../../../commands/update-weekly-working-time.command";
import {EmployeeRepository} from "../../../domain/repositories/employee.repository";
import {BulkCommand} from "../../../commands/bulk.command";
import {ArrayHelper} from "../../../helpers/array.helper";

@customElement(`ha-dialog-employee-edit`)
export class HaDialogEmployeeEditComponent extends AbstractEntityDialogComponent<Employee> {

    public title = get('ha-boards-view.edit-employee.header');
    protected _formSettings: Array<FormConfiguration<Employee>> = [
        {
            key: `firstName`,
            type: String,
            initialValue: ``,
            validators: [],
            label: `Vorname`
        },
        {
            key: `lastName`,
            type: String,
            initialValue: ``,
            validators: [],
            label: `Nachname`
        },
        {
            key: `shortcut`,
            type: String,
            initialValue: ``,
            validators: [],
            label: `Kürzel`
        },
        {
            key: `color`,
            type: String,
            initialValue: `#FFFFFF`,
            validators: [],
            label: `Farbe`
        },
        {
            key: `weeklyWorkingTime`,
            type: [
                {
                    key: `monday`,
                    type: Number,
                    initialValue: 8,
                    validators: [new NotEmpty()],
                    label: `Montag`
                },
                {
                    key: `tuesday`,
                    type: Number,
                    initialValue: 8,
                    validators: [new NotEmpty()],
                    label: `Dienstag`
                },
                {
                    key: `wednesday`,
                    type: Number,
                    initialValue: 8,
                    validators: [new NotEmpty()],
                    label: `Mittwoch`
                },
                {
                    key: `thursday`,
                    type: Number,
                    initialValue: 8,
                    validators: [new NotEmpty()],
                    label: `Donnerstag`
                },
                {
                    key: `friday`,
                    type: Number,
                    initialValue: 8,
                    validators: [new NotEmpty()],
                    label: `Freitag`
                },
                {
                    key: `saturday`,
                    type: Number,
                    initialValue: 0,
                    validators: [new NotEmpty()],
                    label: `Samstag`
                },
                {
                    key: `sunday`,
                    type: Number,
                    initialValue: 0,
                    validators: [new NotEmpty()],
                    label: `Sonntag`
                },
            ],
            label: `Wochenarbeitszeit`,
            validators: [],
            initialValue: {},
        }
    ]

    protected _renderFormFooter(): unknown {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveButton}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveAndCloseButton}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker secondary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickResetButton}
                
                        data-label=${translate('general.reset')}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    protected _renderMainContent(): unknown {
        const bindTo = this.form!.bindToFn;

        return html`
            <ha-form-employee>                  
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    <div class="wrapper__names">
                        <ha-input-text data-type="first-name" class="max9999-flexrow"
                                        label=${translate('ha-boards-view.edit-employee.first-name')}
                                        ${bindTo(`firstName`)}
                        ></ha-input-text>
                        <ha-input-text data-type="last-name" class="max9999-flexrow"
                                        label=${translate('ha-boards-view.edit-employee.last-name')}
                                        ${bindTo(`lastName`)}
                        ></ha-input-text>
                    </div>
                    <ha-input-text data-type="shortcut" class="max9999-flexrow"
                                    label=${translate('components.ha-dialog.shortcut')}
                                    maxlength="10"
                                    ${bindTo(`shortcut`)}
                    ></ha-input-text>
                    <ha-input-color data-type="color" class="max9999-flexrow"
                                    label=${translate('components.ha-dialog.color')}
                                    .entity=${Employee}
                                    ${bindTo(`color`)}
                    ></ha-input-color>
                    <div class="wrapper__workingtimes">
                        <div class="label-workingtime">Verfügbare Arbeitszeit [Durchschnitt]</div>
                        <ha-input-number data-type="monday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.monday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.monday`)}
                        >
                        </ha-input-number>
                        <ha-input-number data-type="tuesday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.tuesday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.tuesday`)}
                        >
                        </ha-input-number>
                        <ha-input-number data-type="wednesday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.wednesday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.wednesday`)}
                        >
                        </ha-input-number>
                        <ha-input-number data-type="thursday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.thursday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.thursday`)}
                        >
                        </ha-input-number>
                        <ha-input-number data-type="friday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.friday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.friday`)}
                        >
                        </ha-input-number>
                        <ha-input-number data-type="saturday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.saturday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.saturday`)}
                        >
                        </ha-input-number>
                        <ha-input-number data-type="sunday" class="max9999-flexrow"
                                            label=${translate('components.ha-dialog.weeklyWorkingTime.sunday')}
                                            postfix="h"
                                            ${bindTo(`weeklyWorkingTime.sunday`)}
                        >
                        </ha-input-number>
                    </div>    
                </main>
            </ha-form-employee>
        `
    }

    private _onClickResetButton(): void {
        this.form?.reset();
    }

    /** Saves the entity and dispatches a CLOSE command to dismiss the dialog. */
    private async _onClickSaveAndCloseButton(): Promise<void> {
        await this._onClickSaveButton();
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }

    private async _onClickSaveButton(): Promise<void> {
        const data = this.form!.getChangedValues();
        if (!this._entity) {
            return;
        }

        const employee = this._entity;
        const commands: ICommand[] = [];

        if (Reflect.has(data, `weeklyWorkingTime`)) {
            const weeklyWorkingTimeData = Reflect.get(data, `weeklyWorkingTime`);
            const weeklyWorkingTime = await employee.weeklyWorkingTime;
            const weeklyWorkingTimeRepository = container.resolve(WeeklyWorkingTimeRepository);

            weeklyWorkingTimeRepository.update(weeklyWorkingTime, weeklyWorkingTimeData);
            const weeklyWorkingTimeCommand = weeklyWorkingTimeRepository.isNew(weeklyWorkingTime)
                ? new CreateWeeklyWorkingTimeCommand(weeklyWorkingTime)
                : new UpdateWeeklyWorkingTimeCommand(weeklyWorkingTime);

            commands.push(weeklyWorkingTimeCommand);

            Reflect.deleteProperty(data, `weeklyWorkingTime`);
        }

        if (Object.keys(data).length > 0) {

            Object.assign(employee, data);

            const employeeRepository = container.resolve(EmployeeRepository);
            const employeeCommand = employeeRepository.isNew(employee)
                ? new CreateEmployeeCommand(employee)
                : new RenameEmployeeCommand(employee);

            commands.push(employeeCommand);
        }

        commands.reverse();

        this.dispatchCommand(<DialogCommand<ICommand<Employee>>>{
            command: DialogCommandName.OKAY,
            payload: commands.length > 1
                ? new BulkCommand(commands)
                : ArrayHelper.first(commands)
        });
    }
}
